import { SkuApiDto } from '@b2x/storefront-api-js-client/src/';
import classnames from 'classnames';

import { Div } from './HTMLElement';
import { t } from './i18n/i18n';
import { useStockAvailability } from './useStockAvailability';
import { PropsWithCustomComponent, VariantsController } from './VariantsController';

export interface StockAvailabilityProps {
  children?(availability: number | undefined): React.ReactNode;
  className?: string;
  hideOverThreshold?: boolean;
  label?: {
    inStock?: string;
    inThreshold?: string;
    outOfStock?: string;
  };
  sku: SkuApiDto;
  threshold?: number;
}

const StockAvailability = ({
  children,
  className,
  hideOverThreshold = true,
  label,
  sku,
  threshold = 30,
}: StockAvailabilityProps) => {
  const { availability } = useStockAvailability(sku);

  const inStockLabel = label?.inStock || t('product.availability.inStock', { availability });
  const inThresholdLabel = label?.inThreshold || t('product.availability.inThreshold', { availability });
  const outOfStockLabel = label?.outOfStock || t('product.availability.outOfStock');

  const _label =
    availability && availability > threshold
      ? inStockLabel
      : availability && availability <= threshold
        ? inThresholdLabel
        : outOfStockLabel;

  if (hideOverThreshold && availability && availability > threshold) {
    return null;
  }

  return (
    <Div
      className={classnames('stock-availability', className)}
      uiClassName={{
        bs5: classnames({
          'text-danger': !availability,
          'text-success': availability && availability > threshold,
          'text-warning': availability && availability <= threshold && availability > 0,
        }),
      }}
    >
      {children ? children(availability) : _label}
    </Div>
  );
};

export type StockAvailabilityVariants = '';

const StockAvailabilityController = (props: PropsWithCustomComponent<StockAvailabilityProps>) => (
  <VariantsController<StockAvailabilityProps, StockAvailabilityVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: StockAvailability,
      name: 'StockAvailability',
    }}
  />
);
export { StockAvailabilityController as StockAvailability };
